import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { APIResponse, APIError, RetryConfig } from '../types/api';
import { env } from '../config';

/**
 * API Client Configuration
 */
export interface APIClientConfig {
  baseURL: string;
  timeout?: number;
  headers?: Record<string, string>;
  retry?: RetryConfig;
  interceptors?: {
    request?: (config: AxiosRequestConfig) => AxiosRequestConfig;
    response?: (response: AxiosResponse) => AxiosResponse;
    error?: (error: any) => Promise<any>;
  };
}

/**
 * Enhanced API Client
 * 
 * Provides a comprehensive HTTP client with retry logic,
 * error handling, and response transformation.
 */
export class APIClient {
  private client: AxiosInstance;

  private retryConfig?: RetryConfig;

  constructor(config: APIClientConfig) {
    this.retryConfig = config.retry;
    
    this.client = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 10000,
      headers: {
        'Content-Type': 'application/json',
        ...config.headers,
      },
    });

    this.setupInterceptors(config.interceptors);
  }

  private setupInterceptors(interceptors?: APIClientConfig['interceptors']) {
    // Request interceptor
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Add request timestamp for monitoring
        (config as any).metadata = { startTime: Date.now() };
        
        if (interceptors?.request) {
          return interceptors.request(config as AxiosRequestConfig) as InternalAxiosRequestConfig;
        }
        return config;
      },
      (error) => Promise.reject(this.transformError(error))
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        // Calculate request duration for monitoring (unused in current implementation)
        
        if (interceptors?.response) {
          return interceptors.response(response);
        }
        
        return response;
      },
      async (error) => {
        // Handle retry logic
        if (this.shouldRetry(error)) {
          return this.retryRequest(error);
        }

        if (interceptors?.error) {
          return interceptors.error(error);
        }

        return Promise.reject(this.transformError(error));
      }
    );
  }

  private transformError(error: any): APIError {
    const apiError: APIError = {
      code: error.code || 'UNKNOWN_ERROR',
      message: error.message || 'An unknown error occurred',
      timestamp: new Date(),
      statusCode: error.response?.status || 0,
      type: this.getErrorType(error),
    };

    if (error.response?.data) {
      apiError.details = error.response.data.message || error.response.data.detail;
      apiError.fieldErrors = error.response.data.fieldErrors;
    }

    return apiError;
  }

  private getErrorType(error: any): APIError['type'] {
    const status = error.response?.status;
    
    if (!status) return 'network_error';
    if (status === 401) return 'authentication';
    if (status === 403) return 'authorization';
    if (status === 404) return 'not_found';
    if (status === 422) return 'validation';
    if (status === 429) return 'rate_limit';
    if (status >= 500) return 'server_error';
    
    return 'unknown';
  }

  private shouldRetry(error: any): boolean {
    if (!this.retryConfig) return false;
    
    const status = error.response?.status;
    const retryableStatuses = this.retryConfig.retryOnStatus || [500, 502, 503, 504, 429];
    
    return retryableStatuses.includes(status);
  }

  private async retryRequest(error: any): Promise<any> {
    const {config} = error;
    config.retryCount = config.retryCount || 0;

    if (config.retryCount >= (this.retryConfig?.maxAttempts || 3)) {
      return Promise.reject(error);
    }

    config.retryCount++;

    const delay = Math.min(
      (this.retryConfig?.delay || 1000) * (this.retryConfig?.backoffMultiplier || 2)**(config.retryCount - 1),
      this.retryConfig?.maxDelay || 30000
    );

    await this.sleep(delay);
    return this.client(config);
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }


  // Public API methods
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    return this.client.get(url, config);
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    return this.client.post(url, data, config);
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    return this.client.put(url, data, config);
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    return this.client.patch(url, data, config);
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    return this.client.delete(url, config);
  }
}

/**
 * Create a new API client instance
 */
export function createAPIClient(config: APIClientConfig): APIClient {
  return new APIClient(config);
}

/**
 * WordPress API Base URL — runtime setting first, env fallback (PRP-10).
 *
 * The baseURL prefers wordpress.api_url from /api/config/public so an admin can
 * repoint WordPress without a rebuild, RACED against a short timeout so a slow
 * or absent API (static forks) never delays WP page loads beyond ~1.5s on the
 * first call. Resolution happens ONCE per session; the client is then cached.
 */
async function resolveBaseURL(): Promise<string> {
  try {
    const config = await Promise.race([
      fetch('/api/config/public').then((r) => (r.ok ? r.json() : null)),
      new Promise<null>((resolve) => {
        setTimeout(() => resolve(null), 1500);
      }),
    ]);
    const runtime = (config as { wordpress?: { apiUrl?: string | null } } | null)?.wordpress?.apiUrl;
    if (typeof runtime === 'string' && runtime.trim()) return runtime;
  } catch {
    /* env fallback below */
  }
  return env.VITE_API_BASE_URL;
}

let clientPromise: Promise<APIClient> | null = null;

/** Lazy singleton WordPress client (call sites await internally). */
export function getClient(): Promise<APIClient> {
  clientPromise ??= resolveBaseURL().then((baseURL) =>
    createAPIClient({
      baseURL,
      timeout: 10000,
      retry: {
        maxAttempts: 3,
        delay: 1000,
        backoffMultiplier: 2,
        maxDelay: 10000,
        retryOnStatus: [500, 502, 503, 504, 429],
      },
    })
  );
  return clientPromise;
}

/**
 * WordPress Post interface
 * 
 * Represents a WordPress post with all standard fields plus embedded media.
 * Note: title, content, and excerpt are objects with 'rendered' property
 * as they come from WordPress REST API already processed.
 */
export interface Post {
  /** Unique post identifier */
  id: number;
  /** URL-friendly post identifier */
  slug: string;
  /** Post title (HTML rendered) */
  title: { rendered: string };
  /** Post content (HTML rendered) */
  content: { rendered: string };
  /** Post excerpt (HTML rendered) */
  excerpt: { rendered: string };
  /** Post creation date (ISO string) */
  date: string;
  /** Post last modified date (ISO string) */
  modified: string;
  /** Optional embedded media data */
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url: string;
    }>;
    author?: Array<{
      name: string;
    }>;
    'wp:term'?: Array<Array<{ id: number; name: string }>>;
  };
}

/**
 * WordPress Page interface
 * 
 * Similar to Post but represents static pages rather than blog posts.
 * Contains same basic structure with rendered HTML content.
 */
export interface Page {
  /** Unique page identifier */
  id: number;
  /** URL-friendly page identifier */
  slug: string;
  /** Page title (HTML rendered) */
  title: { rendered: string };
  /** Page content (HTML rendered) */
  content: { rendered: string };
  /** Page excerpt (HTML rendered) */
  excerpt?: { rendered: string };
  /** Page creation date (ISO string) */
  date: string;
  /** Page last modified date (ISO string) */
  modified: string;
  /** Optional embedded media data */
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url: string;
    }>;
  };
}

/**
 * Fetch WordPress posts with pagination
 * 
 * @param page - Page number to fetch (1-based)
 * @param perPage - Number of posts per page
 * @returns Promise resolving to array of Post objects
 * 
 * @throws {Error} When API request fails
 */
export const getPosts = async (page = 1, perPage = 10): Promise<Post[]> => {
  const client = await getClient();
  const response = await client.get<Post[]>('/wp/v2/posts', {
    params: {
      page,
      per_page: perPage,
      _embed: true, // Include featured media and other embedded content
    },
  });
  return response.data;
};

/**
 * Fetch a single WordPress post by slug
 * 
 * @param slug - The post slug to search for
 * @returns Promise resolving to a single Post object
 * 
 * @throws {Error} When post with given slug is not found
 */
export const getPostBySlug = async (slug: string): Promise<Post> => {
  const client = await getClient();
  const response = await client.get<Post[]>('/wp/v2/posts', {
    params: {
      slug,
      _embed: true, // Include featured media and other embedded content
    },
  });
  
  if (!response.data.length) {
    throw new Error(`Post with slug "${slug}" not found`);
  }
  
  return response.data[0];
};

/**
 * Fetch a single WordPress page by slug
 * 
 * @param slug - The page slug to search for
 * @returns Promise resolving to a single Page object
 * 
 * @throws {Error} When page with given slug is not found
 */
export const getPageBySlug = async (slug: string): Promise<Page> => {
  const client = await getClient();
  const response = await client.get<Page[]>('/wp/v2/pages', {
    params: {
      slug,
      _embed: true, // Include featured media and other embedded content
    },
  });
  
  if (!response.data.length) {
    throw new Error(`Page with slug "${slug}" not found`);
  }
  
  return response.data[0];
}; 